import { usePortalGuard } from '@glplus/glpweb-shared-libs';
import { Routes, Route } from 'react-router';
import { AppShell } from './components/AppShell';
import HomePage from './routes/HomePage';
import MissingUpcQueue from './routes/MissingUpcQueue';
import MissingManufacturers from './routes/MissingManufacturers';
import ProductImageQueue from './routes/ProductImageQueue';
import UpcReviewQueue from './routes/UpcReviewQueue';
import AuditLog from './routes/AuditLog';
import StoreRequests from './routes/StoreRequests';
import RecipeModeration from './routes/RecipeModeration';
import CommentModeration from './routes/CommentModeration';
import RecallConsole from './routes/RecallConsole';
import UserManagement from './routes/UserManagement';
import StaffManagement from './routes/StaffManagement';
import FaqManagement from './routes/FaqManagement';
import VideoManagement from './routes/VideoManagement';
import SupportRequests from './routes/SupportRequests';
import RelationshipReports from './routes/RelationshipReports';
import MailingList from './routes/MailingList';
import JobRunner from './routes/JobRunner';
import RunnerApplications from './routes/RunnerApplications';
import RunnerApplicationReview from './routes/RunnerApplicationReview';
import RunnerLimitRequests from './routes/RunnerLimitRequests';
import CoverQueue from './routes/CoverQueue';
import OrdersNeedingCover from './routes/OrdersNeedingCover';
import OrderingReviews from './routes/OrderingReviews';
import OrderReviews from './routes/OrderReviews';
import RunnerLimitReview from './routes/RunnerLimitReview';
import RunnerPhotoQueue from './routes/RunnerPhotoQueue';
import RunnerInsuranceQueue from './routes/RunnerInsuranceQueue';
import ProductEditor from './routes/ProductEditor';
import ProductEditDetail from './routes/ProductEditDetail';
import ProductDataReports from './routes/ProductDataReports';
import VariantDedup from './routes/VariantDedup';
import Login from './routes/Login';

const PORTAL_URLS = {
  consumer: import.meta.env.VITE_PORTAL_CONSUMER_URL as string,
  driver: import.meta.env.VITE_PORTAL_DRIVER_URL as string,
  vendor: import.meta.env.VITE_PORTAL_VENDOR_URL as string,
  admin: import.meta.env.VITE_PORTAL_ADMIN_URL as string,
};

const LOGIN_URL = import.meta.env.VITE_LOGIN_URL as string;

export default function App() {
  return (
    <Routes>
      {/* /login renders OUTSIDE the guard — the guard redirects here, so
          guarding it would loop. */}
      <Route path="/login" element={<Login />} />
      <Route path="/*" element={<GuardedApp />} />
    </Routes>
  );
}

function GuardedApp() {
  const guard = usePortalGuard({
    requiredPortal: 'admin',
    portalUrls: PORTAL_URLS,
    loginUrl: LOGIN_URL,
    // Pool-per-actor (2026-07-04): the portal authenticates against the
    // EMPLOYEE pool, which has NO groups — pool membership (admin-create-only)
    // is the perimeter, so any valid session is admitted. Fine-grained access
    // is enforced server-side by admin-api (GLPE row → capabilities), and the
    // nav renders from /admin/me/capabilities.
    admitAnyAuthenticated: true,
    // Local-iteration escape hatch (Runbook AM). Set VITE_DISABLE_PORTAL_GUARD=true
    // in .env.local to render the portal without auth. NEVER set in a deployed build.
    disabled: import.meta.env.VITE_DISABLE_PORTAL_GUARD === 'true',
  });

  if (guard.isLoading) {
    return <Loading message="Checking access…" />;
  }
  if (guard.status !== 'authorized') {
    // Hook is redirecting via window.location.assign. Render a brief
    // placeholder while the navigation commits.
    return <Loading message="Redirecting…" />;
  }

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/missing-upc" element={<MissingUpcQueue />} />
        <Route path="/missing-manufacturers" element={<MissingManufacturers />} />
        <Route path="/product-images" element={<ProductImageQueue />} />
        <Route path="/upc-review" element={<UpcReviewQueue />} />
        <Route path="/store-requests" element={<StoreRequests />} />
        <Route path="/recipes" element={<RecipeModeration />} />
        <Route path="/comments" element={<CommentModeration />} />
        <Route path="/recalls" element={<RecallConsole />} />
        <Route path="/users" element={<UserManagement />} />
        <Route path="/staff" element={<StaffManagement />} />
        <Route path="/faqs" element={<FaqManagement />} />
        <Route path="/videos" element={<VideoManagement />} />
        <Route path="/support" element={<SupportRequests />} />
        <Route path="/relationship-reports" element={<RelationshipReports />} />
        <Route path="/mailing-list" element={<MailingList />} />
        <Route path="/jobs" element={<JobRunner />} />
        <Route path="/runner-applications" element={<RunnerApplications />} />
        <Route path="/runner-applications/:id" element={<RunnerApplicationReview />} />
        <Route path="/runner-photos" element={<RunnerPhotoQueue />} />
        <Route path="/runner-insurance" element={<RunnerInsuranceQueue />} />
        <Route path="/cover-queue" element={<CoverQueue />} />
        <Route path="/orders-needing-cover" element={<OrdersNeedingCover />} />
        <Route path="/ordering-reviews" element={<OrderingReviews />} />
        <Route path="/order-reviews" element={<OrderReviews />} />
        <Route path="/runner-limits" element={<RunnerLimitRequests />} />
        <Route path="/runner-limits/requests/:id" element={<RunnerLimitReview />} />
        <Route path="/runner-limits/runners/:runnerId" element={<RunnerLimitReview />} />
        <Route path="/product-editor" element={<ProductEditor />} />
        <Route path="/product-data-reports" element={<ProductDataReports />} />
        <Route path="/product-editor/variants" element={<VariantDedup />} />
        <Route path="/product-editor/:glpProductId" element={<ProductEditDetail />} />
        <Route path="/audit-log" element={<AuditLog />} />
        <Route path="*" element={<HomePage />} />
      </Routes>
    </AppShell>
  );
}

function Loading({ message }: { message: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        fontFamily: 'system-ui, sans-serif',
        color: '#555',
      }}
    >
      {message}
    </div>
  );
}
